import { FormQuestions, FormSectionContainer, WarningSummarySection } from '@form';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { scrollToTopOfPage, setTitle } from '@util/browser';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Api from '@services/api';
import { Button } from 'nhsuk-react-components';
import { createSectionWarningListFromProps } from '@util/warning-summary';
import { getSubmissionSectionPayload } from '@util/api-payloads';
import { isEmpty } from 'lodash';

const FormQuestionSection = forwardRef(props => {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [state, setState] = useState({
        multipleSectionIndex: undefined,
        showRemoveButton: undefined,
        showSaveAndExitButton: undefined,
        showSaveAndSkipButton: undefined,
        showSaveAndGoToSummaryButton: undefined,
        showSkipButton: undefined,
        title: undefined,
        warningList: []
    });

    const [, setHcpcState] = useState({
        typedValue: '',
        acceptedRecord: null
    });

    const handleHcpcValidationChange = useCallback(data => {
        setHcpcState({
            typedValue: data.typedValue,
            acceptedRecord: data.acceptedRecord
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const index = params.index;
        var warningList;
        //handleChange();
        if (!props.isFirstLoad) {
            warningList = createSectionWarningListFromProps(props);
        }

        setState({
            multipleSectionIndex: index ? Number(index) : undefined,
            showRemoveButton:
                props.sectionSpec.allow_multiple &&
                !props.isFirstLoad &&
                !(
                    props.sectionSpec?.multiple_sections?.must_have &&
                    props.multipleSectionCount === 1
                ),
            showSaveAndExitButton: props.showSaveAndExitButton,
            showSaveAndSkipButton: props.showSaveAndSkipButton,
            showSaveAndGoToSummaryButton:
                props.showSaveAndGoToSummaryButton && !props.sectionSpec.allow_multiple,
            showSkipButton:
                props.sectionSpec.allow_multiple &&
                props.isFirstLoad &&
                !props.sectionSpec?.multiple_sections?.hide_skip_button,
            title: `${props.sectionSpec.name} - ${props.collection.name}`,
            warningList
        });
    }, [params.index, props]);
    // eslint-disable-next-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const validateQuestions = () => {
        props.questions.forEach(question => {
            const shouldValidate =
                question.hidden !== true &&
                question.includeInApiPayload &&
                (question.mandatory === true || question.required === true);
            if (
                shouldValidate &&
                question.ref?.current != null &&
                typeof question.ref.current.validate === 'function'
            ) {
                question.ref.current.validate();
            }
        });
    };
    const validateSection = useCallback(() => {
        validateQuestions();
        // eslint-disable-line react-hooks/exhaustive-deps
    }, [validateQuestions]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const isSectionValid = () => {
        let isValid = true;

        props.questions.forEach(question => {
            const hasValue = String(question.value ?? '').trim().length > 0;

            const shouldBlockNavigation =
                question.hidden !== true &&
                question.includeInApiPayload &&
                (question.mandatory === true || (question.required === true && hasValue));

            if (!shouldBlockNavigation) {
                return;
            }

            if (question.ref?.current && typeof question.ref.current.isValid !== 'undefined') {
                if (!question.ref.current.isValid) {
                    isValid = false;
                }
            } else if (question.isValid === false) {
                isValid = false;
            }
        });

        return isValid;
    };

    const createNewSubmission = useCallback(
        async payload => {
            payload.org_code = props.orgCode;

            const api = new Api();
            const response = await api.postSubmission(payload);

            props.onSubmissionIdChange(response?.data?.id);
        },
        [props, props.orgCode] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const updateExistingSubmission = useCallback(
        async payload => {
            const api = new Api();
            const id = props.submissionId;
            const multipleSectionIndex = state.multipleSectionIndex;

            if (props.sectionSpec.allow_multiple) {
                payload.section.index = multipleSectionIndex - 1;
            }
            await api.patchSubmission(payload, id);
        },
        [props, state.multipleSectionIndex] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const sendData = useCallback(async () => {
        const payload = {
            section: {
                id: props.sectionSpec.id,
                questions: getSubmissionSectionPayload(props.questions)
            }
        };
        if (props.submissionId) {
            await updateExistingSubmission(payload);
        } else {
            await createNewSubmission(payload);
        }
    }, [props, createNewSubmission, updateExistingSubmission]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitData = useCallback(async () => {
        props.onSkipSections({
            sectionId: props.sectionSpec.id,
            nextSectionId: props.nextSectionId
        });

        await sendData();

        props.onSectionSubmit({
            index: state.multipleSectionIndex,
            sectionId: props.sectionSpec.id
        });
    }, [props, sendData, state.multipleSectionIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    const navigateTo = useCallback(
        path => {
            navigate(path);
            scrollToTopOfPage();
        },
        [navigate]
    );

    const navigateToNextSection = useCallback(() => {
        if (props.sectionSpec.allow_multiple) {
            if (props.sectionSpec?.multiple_sections?.enable_skip_to_add_another) {
                navigateTo(`${location.pathname}#add-another`);
                return;
            }
            const multipleSectionIndex = state.multipleSectionIndex;
            const multipleSectionCount = props.multipleSectionCount;

            if (multipleSectionIndex < multipleSectionCount) {
                const collectionUrlSlug = params.collection;
                const sectionId = params.section;

                navigateTo(`/${collectionUrlSlug}/${sectionId}/${multipleSectionIndex + 1}`);
            } else {
                navigateTo(`${location.pathname}#add-another`);
            }
        } else {
            navigateTo(props.nextPath);
        }
    }, [props, state.multipleSectionIndex, location.pathname, navigateTo, params]); // eslint-disable-line react-hooks/exhaustive-deps

    const waitForValidationState = () =>
        new Promise(resolve => {
            setTimeout(resolve, 0);
        });

    const handleSubmit = useCallback(
        async event => {
            event.preventDefault();
            await event.currentTarget.blur();
            validateSection();

            await waitForValidationState();

            const sectionValid = isSectionValid();

            if (!sectionValid) {
                return;
            }

            try {
                await submitData();
            } catch (error) {
                return;
            }

            navigateToNextSection();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [validateSection, isSectionValid, submitData, navigateToNextSection, props.questions]
    );

    const getFormQuestionValue = questionId => {
        for (const section of props.formData || []) {
            const question = section.questions?.find(q => q.id === questionId);
            if (question) {
                return question.value;
            }
        }

        return undefined;
    };

    const handleSaveAndExit = useCallback(
        async event => {
            event.preventDefault();

            validateSection();
            await waitForValidationState();

            if (!isSectionValid()) {
                return;
            }

            await submitData();
            props.onResetForm();
            navigateTo(`/${params.collection}`);
        },
        [validateSection, isSectionValid, submitData, navigateTo, props, params]
    );

    const handleSaveAndSkipButton = useCallback(
        async event => {
            event.preventDefault();
            await event.currentTarget.blur();

            validateSection();
            await waitForValidationState();

            if (!isSectionValid()) {
                return;
            }

            await submitData();
            navigateTo(`/${params.collection}/summary`);
        },
        [validateSection, isSectionValid, submitData, navigateTo, params]
    );

    const handleChange = useCallback(
        event => {
            props.onQuestionChange({
                index: state.multipleSectionIndex,
                sectionId: props.sectionSpec.id,
                questions: props.questions,
                ...event
            });

            const warningList = createSectionWarningListFromProps(props);
            setState(prevState => ({ ...prevState, warningList }));
        },
        [props, state.multipleSectionIndex] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleConditionalReveal = useCallback(
        event => {
            props.onConditionalReveal({
                index: state.multipleSectionIndex,
                sectionId: props.sectionSpec.id,
                ...event
            });
        },
        [props, state.multipleSectionIndex] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleDisable = useCallback(
        event => {
            props.handleDisable({
                index: state.multipleSectionIndex,
                sectionId: props.sectionSpec.id,
                ...event
            });
        },
        [props, state.multipleSectionIndex] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleSkip = useCallback(() => {
        props.onSkipSection({
            index: state.multipleSectionIndex,
            sectionId: props.sectionSpec.id
        });

        navigateTo(props.nextPath);
    }, [props, state.multipleSectionIndex, navigateTo]); // eslint-disable-line react-hooks/exhaustive-deps

    const navigateToAddOrAddAnother = useCallback(() => {
        const multipleSectionCount = props.multipleSectionCount;
        const sectionPath = `/${params.collection}/${params.section}`;

        if (multipleSectionCount === 1) {
            navigateTo(sectionPath);
        } else {
            navigateTo(`${sectionPath}/${multipleSectionCount - 1}#add-another`);
        }
    }, [props, navigateTo, params]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRemove = useCallback(async () => {
        const api = new Api();

        const id = props.submissionId;
        const sectionId = props.sectionSpec.id;
        const multipleSectionIndex = state.multipleSectionIndex;

        props.onRemoveSection({
            index: multipleSectionIndex,
            sectionId: sectionId
        });

        await api.deleteSubmissionSection(id, sectionId, multipleSectionIndex - 1);

        navigateToAddOrAddAnother();
    }, [props, state.multipleSectionIndex, navigateToAddOrAddAnother]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCopyAnswer = useCallback(
        data => {
            const index = state.multipleSectionIndex;
            if (index) {
                data.index = index;
            }
            return props.onCopyAnswer(data);
        },
        [props, state.multipleSectionIndex] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const enrichQuestionPresentation = question => {
        if (question.newQuestion) {
            return (
                <div>
                    <span style={{ color: '#005EB8' }}>
                        <b>[New Question]</b>
                    </span>
                </div>
            );
        }

        if (question.revisedQuestion) {
            return (
                <div>
                    <span style={{ color: '#005EB8' }}>
                        <b>[Revised Question]</b>
                    </span>
                </div>
            );
        }
    };

    const currentGmcValue = getFormQuestionValue('gmc');

    const getFirstFormQuestionValue = questionIds => {
        for (const questionId of questionIds) {
            const value = getFormQuestionValue(questionId);
            if (value) {
                return value;
            }
        }

        return undefined;
    };

    const currentHcpcValue = getFirstFormQuestionValue([
        'direct-staff-role-hcpc',
        'direct-staff-role-pcn-hcpc',
        'direct-staff-role-pcn-pool-hcpc'
    ]);

    const getStaffRoleDisplayValue = () => {
        const staffRoleQuestion = props.questions?.find(q =>
            [
                'direct-staff-role',
                'direct-staff-role-pcn',
                'direct-patient-care-staff-role'
            ].includes(q.id)
        );

        if (!staffRoleQuestion) {
            return undefined;
        }

        const selectedOption = staffRoleQuestion.options?.find(
            option => option.key === staffRoleQuestion.value
        );

        return selectedOption?.value ?? staffRoleQuestion.value;
    };

    const currentStaffRole = getStaffRoleDisplayValue();

    const isPcnPooledCollection = params.collection === 'nhs-workforce-pcn-pooled-roles';

    useEffect(() => {
        setTitle(state.title);
    }, [state.title]);
    return (
        <FormSectionContainer sectionSpec={props.sectionSpec}>
            {!isEmpty(state.warningList) && (
                <WarningSummarySection warningList={state.warningList} />
            )}
            <form key={props.sectionSpec.id}>
                {props.questions
                    .filter(x => x.showOnFormSection)
                    .map((question, index) => (
                        <React.Fragment key={question.id}>
                            {enrichQuestionPresentation(question)}
                            <FormQuestions
                                isFirstLoad={props.isFirstLoad}
                                key={index}
                                onConditionalReveal={handleConditionalReveal}
                                onQuestionChange={handleChange}
                                onCopyAnswer={handleCopyAnswer}
                                orgCode={props.orgCode}
                                question={question}
                                questions={props.questions}
                                handleDisable={handleDisable}
                                formData={props.formData}
                                // itemsRef={props.itemsRef}
                                ref={question.ref}
                                onHcpcValidationChange={handleHcpcValidationChange}
                                enteredDetails={{
                                    firstName: getFormQuestionValue('first-name'),
                                    surname: getFormQuestionValue('last-name'),
                                    gender: getFormQuestionValue('gender'),
                                    profession: currentStaffRole
                                }}
                                gmcValue={currentGmcValue}
                                hcpcValue={currentHcpcValue}
                                skipHcpcPersonValidation={isPcnPooledCollection}
                            />
                        </React.Fragment>
                    ))}
                <hr />

                {state.showSaveAndGoToSummaryButton ? (
                    <Button onClick={handleSaveAndSkipButton} aria-label="Save and go to summary">
                        Save and go to summary
                    </Button>
                ) : (
                    <Button onClick={handleSubmit} aria-label="Save and Continue">
                        Save and Continue
                    </Button>
                )}

                {state.showSaveAndSkipButton && (
                    <Button
                        secondary={true}
                        className="nhsuk-u-margin-left-2"
                        onClick={handleSaveAndSkipButton}
                    >
                        Save and Skip to Summary
                    </Button>
                )}

                {state.showSaveAndExitButton && (
                    <Button
                        secondary={true}
                        className="nhsuk-u-margin-left-2"
                        onClick={handleSaveAndExit}
                    >
                        Save and Exit
                    </Button>
                )}

                {state.showSkipButton && (
                    <Button secondary={true} className="nhsuk-u-margin-left-2" onClick={handleSkip}>
                        Skip
                    </Button>
                )}

                {state.showRemoveButton && (
                    <Button
                        secondary={true}
                        className="nhsuk-u-margin-left-2"
                        onClick={handleRemove}
                        type="button"
                    >
                        Remove
                    </Button>
                )}
            </form>
        </FormSectionContainer>
    );
});

export default FormQuestionSection;
